// Shared skin builder utilities — procedural Three.js character model helpers.
// Used by all skin builder modules.
// V2: More human proportions with smoother geometry, facial features, and muscle definition.
// Crypto themes expressed through clothing, accessories, and armor — not body shape.

import * as THREE from 'three';

/* ------------------------------------------------------------------ */
/*  Utility helpers                                                    */
/* ------------------------------------------------------------------ */

export const SEG = 16; // higher segment count for smoother curves

export function darken(color: number, factor: number): number {
  const r = Math.floor(((color >> 16) & 0xff) * factor);
  const g = Math.floor(((color >> 8) & 0xff) * factor);
  const b = Math.floor((color & 0xff) * factor);
  return (r << 16) | (g << 8) | b;
}

export function lighten(color: number, factor: number): number {
  const r = Math.min(255, Math.floor(((color >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.floor(((color >> 8) & 0xff) * factor));
  const b = Math.min(255, Math.floor((color & 0xff) * factor));
  return (r << 16) | (g << 8) | b;
}

export function mat(c: number, opts?: { roughness?: number; metalness?: number }): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: c,
    roughness: opts?.roughness ?? 0.75,
    metalness: opts?.metalness ?? 0.05,
  });
}

export function matEmissive(c: number, intensity = 2): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: c,
    emissive: c,
    emissiveIntensity: intensity,
    roughness: 0.4,
    metalness: 0.1,
  });
}

export function matSkin(c: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: c,
    roughness: 0.85,
    metalness: 0.02,
  });
}

export function enableShadows(group: THREE.Group): void {
  group.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
}

/* ------------------------------------------------------------------ */
/*  Facial feature builder — adds eyes, nose, mouth, ears, brows      */
/* ------------------------------------------------------------------ */

export interface FaceOpts {
  skinColor: number;
  eyeColor?: number;
  headY: number;
  headRadius: number;
  addEars?: boolean;
  addNose?: boolean;
  addMouth?: boolean;
  addBrows?: boolean;
}

export function addFacialFeatures(group: THREE.Group, opts: FaceOpts): void {
  const hY = opts.headY;
  const hR = opts.headRadius;
  const eyeColor = opts.eyeColor ?? 0x3a6a3a;

  // Eyes — white sclera + colored iris + black pupil
  for (const side of [-1, 1]) {
    const eyeX = side * hR * 0.38;
    const eyeZ = -(hR * 0.88);
    // Sclera
    const sclera = new THREE.Mesh(new THREE.SphereGeometry(hR * 0.16, 10, 10), mat(0xf8f8f0));
    sclera.position.set(eyeX, hY + hR * 0.08, eyeZ);
    sclera.scale.set(1.2, 1.0, 0.5);
    group.add(sclera);
    // Iris
    const iris = new THREE.Mesh(new THREE.SphereGeometry(hR * 0.09, 8, 8), mat(eyeColor));
    iris.position.set(eyeX, hY + hR * 0.08, eyeZ - hR * 0.06);
    group.add(iris);
    // Pupil
    const pupil = new THREE.Mesh(new THREE.SphereGeometry(hR * 0.05, 6, 6), mat(0x050505));
    pupil.position.set(eyeX, hY + hR * 0.08, eyeZ - hR * 0.09);
    group.add(pupil);
  }

  // Eyebrows
  if (opts.addBrows !== false) {
    for (const side of [-1, 1]) {
      const brow = new THREE.Mesh(
        new THREE.BoxGeometry(hR * 0.42, hR * 0.06, hR * 0.12),
        mat(darken(opts.skinColor, 0.45)),
      );
      brow.position.set(side * hR * 0.38, hY + hR * 0.26, -(hR * 0.82));
      brow.rotation.z = side * -0.08;
      group.add(brow);
    }
  }

  // Nose
  if (opts.addNose !== false) {
    const noseBase = new THREE.Mesh(
      new THREE.ConeGeometry(hR * 0.10, hR * 0.22, 6),
      matSkin(darken(opts.skinColor, 0.95)),
    );
    noseBase.position.set(0, hY - hR * 0.06, -(hR * 0.92));
    noseBase.rotation.x = -0.25;
    group.add(noseBase);
    // Nose tip
    const noseTip = new THREE.Mesh(new THREE.SphereGeometry(hR * 0.08, 6, 6), matSkin(darken(opts.skinColor, 0.92)));
    noseTip.position.set(0, hY - hR * 0.16, -(hR * 1.02));
    group.add(noseTip);
  }

  // Mouth
  if (opts.addMouth !== false) {
    const lips = new THREE.Mesh(
      new THREE.BoxGeometry(hR * 0.40, hR * 0.06, hR * 0.08),
      mat(darken(opts.skinColor, 0.82)),
    );
    lips.position.set(0, hY - hR * 0.35, -(hR * 0.86));
    group.add(lips);
    // Lower lip slightly fuller
    const lowerLip = new THREE.Mesh(
      new THREE.SphereGeometry(hR * 0.13, 8, 6, 0, Math.PI * 2, Math.PI * 0.3, Math.PI * 0.3),
      mat(darken(opts.skinColor, 0.78)),
    );
    lowerLip.position.set(0, hY - hR * 0.40, -(hR * 0.86));
    lowerLip.scale.set(1.5, 0.8, 0.5);
    group.add(lowerLip);
  }

  // Ears
  if (opts.addEars !== false) {
    for (const side of [-1, 1]) {
      const ear = new THREE.Mesh(
        new THREE.SphereGeometry(hR * 0.18, 8, 8),
        matSkin(darken(opts.skinColor, 0.92)),
      );
      ear.position.set(side * (hR * 0.92), hY + hR * 0.02, 0);
      ear.scale.set(0.35, 0.7, 0.5);
      group.add(ear);
    }
  }
}

/* ------------------------------------------------------------------ */
/*  FPP head group helper — items added here are hidden in first-person */
/* ------------------------------------------------------------------ */

export function fppHeadGroup(parent: THREE.Group): THREE.Group {
  // Find existing __fpp_hidden__ group or create one
  for (const child of parent.children) {
    if (child.name === '__fpp_hidden__' && child instanceof THREE.Group) return child;
  }
  const hg = new THREE.Group();
  hg.name = '__fpp_hidden__';
  parent.add(hg);
  return hg;
}

/* ------------------------------------------------------------------ */
/*  Shared humanoid base builder — V2 with realistic proportions       */
/* ------------------------------------------------------------------ */

export interface HumanoidOpts {
  bodyColor: number;
  headColor: number;
  headRadius?: number;
  torsoScaleX?: number;
  torsoScaleZ?: number;
  torsoWidth?: number;
  torsoHeight?: number;
  armColor?: number;
  legColor?: number;
  bootColor?: number;
  handColor?: number;
  handRadius?: number;
  shoulderWidth?: number;
  hipWidth?: number;
  headY?: number;
  neckColor?: number;
  lean?: number;
  legSpread?: number;
  overallScale?: number;
  // V2 additions
  skinColor?: number; // for face if different from headColor
  showFace?: boolean; // default true
  eyeColor?: number;
  muscular?: boolean; // adds deltoid/calf definition
  femaleProportions?: boolean; // narrower shoulders, wider hips
}

export function buildHumanoid(opts: HumanoidOpts): THREE.Group {
  const g = new THREE.Group();
  const bc = opts.bodyColor;
  const hc = opts.headColor;
  const ac = opts.armColor ?? bc;
  const lc = opts.legColor ?? darken(bc, 0.8);
  const boc = opts.bootColor ?? darken(bc, 0.3);
  const handc = opts.handColor ?? boc;
  const torsoSX = opts.torsoScaleX ?? 1.2;
  const torsoSZ = opts.torsoScaleZ ?? 0.72;
  const torsoW = opts.torsoWidth ?? 0.18;
  const torsoH = opts.torsoHeight ?? 0.32;
  let sw = opts.shoulderWidth ?? 0.28;
  let hw = opts.hipWidth ?? 0.09;
  const headR = opts.headRadius ?? 0.14;
  const headY = opts.headY ?? 1.66;
  const handR = opts.handRadius ?? 0.038;
  const lean = opts.lean ?? 0;
  const legSpread = opts.legSpread ?? 0;
  const showFace = opts.showFace !== false;
  const skinColor = opts.skinColor ?? hc;
  const muscular = opts.muscular ?? false;
  // F-CM-011: previously declared but never read. Apply documented behavior —
  // narrower shoulders, wider hips — so callers passing femaleProportions
  // get visible silhouette change. Tuned to the suggested deltas from the
  // audit doc; explicit shoulderWidth/hipWidth still override the adjusted
  // defaults via the `??` chain above.
  if (opts.femaleProportions) {
    if (opts.shoulderWidth === undefined) sw -= 0.04;
    if (opts.hipWidth === undefined) hw += 0.02;
  }

  // ─── Head group (hidden in FPP to prevent camera clipping) ───
  const headGroup = new THREE.Group();
  headGroup.name = '__fpp_hidden__';
  g.add(headGroup);

  // ─── Upper torso (chest) ───
  const chest = new THREE.Mesh(new THREE.CapsuleGeometry(torsoW, torsoH * 0.55, 6, SEG), mat(bc));
  chest.name = '__torso__';
  chest.position.set(0, 1.22, 0);
  chest.scale.set(torsoSX, 1.0, torsoSZ);
  g.add(chest);

  // ─── Lower torso (waist) — tapered narrower ───
  const waist = new THREE.Mesh(
    new THREE.CylinderGeometry(torsoW * 0.85, torsoW * torsoSX * 0.75, torsoH * 0.45, SEG),
    mat(darken(bc, 0.92)),
  );
  waist.position.set(0, 0.98, 0);
  waist.scale.set(1.0, 1.0, torsoSZ * 0.85);
  g.add(waist);

  // ─── Chest definition (pec region) ───
  if (muscular) {
    for (const side of [-1, 1]) {
      const pec = new THREE.Mesh(new THREE.SphereGeometry(0.08, 8, 8), mat(lighten(bc, 1.05)));
      pec.position.set(side * 0.08, 1.26, -0.12);
      pec.scale.set(1.2, 0.8, 0.4);
      g.add(pec);
    }
  }

  // ─── Neck — slightly thicker, more human ───
  const neck = new THREE.Mesh(
    new THREE.CylinderGeometry(0.055, 0.065, 0.10, SEG),
    matSkin(opts.neckColor ?? skinColor),
  );
  neck.position.set(0, 1.50, 0);
  headGroup.add(neck);

  // ─── Head — slightly oval, not perfectly spherical ───
  const head = new THREE.Mesh(new THREE.SphereGeometry(headR, SEG, SEG), matSkin(hc));
  head.position.set(0, headY, 0);
  head.scale.set(1.0, 1.08, 0.96); // taller, slightly less deep
  headGroup.add(head);

  // ─── Jawline — subtle box for defined jaw ───
  const jawWidth = headR * 0.85;
  const jaw = new THREE.Mesh(
    new THREE.BoxGeometry(jawWidth * 2, headR * 0.28, headR * 0.7),
    matSkin(darken(hc, 0.97)),
  );
  jaw.position.set(0, headY - headR * 0.48, -headR * 0.08);
  headGroup.add(jaw);

  // ─── Facial features ───
  if (showFace) {
    addFacialFeatures(headGroup, {
      skinColor,
      eyeColor: opts.eyeColor,
      headY,
      headRadius: headR,
    });
  }

  // ─── Arms with better proportions ───
  for (const side of [-1, 1]) {
    const armPivot = new THREE.Group();
    armPivot.name = side === 1 ? '__right_arm_pivot__' : '__left_arm_pivot__';
    armPivot.position.set(side * sw, 1.42, 0);

    // Deltoid cap (shoulder roundness)
    const deltoid = new THREE.Mesh(new THREE.SphereGeometry(0.058, 8, 8), mat(ac));
    deltoid.position.set(0, 0, 0);
    deltoid.scale.set(1.0, 0.7, 0.9);
    armPivot.add(deltoid);

    // Upper arm — tapered (thicker at top)
    const upperArm = new THREE.Mesh(
      new THREE.CylinderGeometry(0.042, 0.050, 0.20, SEG),
      mat(ac),
    );
    upperArm.position.set(0, -0.14, 0);
    armPivot.add(upperArm);

    // Elbow joint
    const elbow = new THREE.Mesh(new THREE.SphereGeometry(0.042, 8, 8), mat(darken(ac, 0.9)));
    elbow.position.set(0, -0.26, 0);
    armPivot.add(elbow);

    // Forearm — tapered (thicker at top, thinner at wrist)
    const forearm = new THREE.Mesh(
      new THREE.CylinderGeometry(0.032, 0.044, 0.20, SEG),
      mat(darken(ac, 0.88)),
    );
    forearm.position.set(0, -0.40, 0);
    armPivot.add(forearm);

    // Wrist
    const wrist = new THREE.Mesh(new THREE.SphereGeometry(0.032, 6, 6), mat(handc));
    wrist.position.set(0, -0.52, 0);
    armPivot.add(wrist);

    // Hand — palm + finger suggestion
    const palm = new THREE.Mesh(
      new THREE.BoxGeometry(handR * 2.2, handR * 2.4, handR * 1.4),
      mat(handc),
    );
    palm.position.set(0, -0.58, 0);
    armPivot.add(palm);

    // Finger cluster (4 fingers grouped)
    const fingers = new THREE.Mesh(
      new THREE.BoxGeometry(handR * 2.0, handR * 1.6, handR * 1.2),
      mat(darken(handc, 0.92)),
    );
    fingers.position.set(0, -0.64, 0);
    armPivot.add(fingers);

    // Thumb
    const thumb = new THREE.Mesh(
      new THREE.CapsuleGeometry(handR * 0.35, handR * 0.8, 3, 6),
      mat(handc),
    );
    thumb.position.set(side * handR * 1.4, -0.56, -handR * 0.6);
    thumb.rotation.z = side * 0.4;
    armPivot.add(thumb);

    g.add(armPivot);
  }

  // ─── Legs with better proportions ───
  for (const side of [-1, 1]) {
    const legPivot = new THREE.Group();
    legPivot.name = side === 1 ? '__right_leg_pivot__' : '__left_leg_pivot__';
    legPivot.position.set(side * (hw + legSpread), 0.90, 0);

    // Hip joint
    const hip = new THREE.Mesh(new THREE.SphereGeometry(0.065, 8, 8), mat(lc));
    hip.position.set(0, 0, 0);
    hip.scale.set(1.0, 0.7, 0.9);
    legPivot.add(hip);

    // Thigh — tapers down
    const thigh = new THREE.Mesh(
      new THREE.CylinderGeometry(0.052, 0.068, 0.24, SEG),
      mat(lc),
    );
    thigh.position.set(0, -0.18, 0);
    legPivot.add(thigh);

    // Knee joint
    const knee = new THREE.Mesh(new THREE.SphereGeometry(0.052, 8, 8), mat(darken(lc, 0.88)));
    knee.position.set(0, -0.32, 0);
    legPivot.add(knee);

    // Shin/calf — tapers down
    const shin = new THREE.Mesh(
      new THREE.CylinderGeometry(0.038, 0.050, 0.22, SEG),
      mat(darken(lc, 0.82)),
    );
    shin.position.set(0, -0.48, 0);
    legPivot.add(shin);

    // Calf muscle (back bulge)
    if (muscular) {
      const calf = new THREE.Mesh(new THREE.SphereGeometry(0.035, 6, 6), mat(darken(lc, 0.85)));
      calf.position.set(0, -0.40, 0.03);
      calf.scale.set(0.8, 1.2, 0.6);
      legPivot.add(calf);
    }

    g.add(legPivot);
  }

  // ─── Boots — more shoe-shaped ───
  for (const side of [-1, 1]) {
    const bx = side * (hw + legSpread);
    // Boot shaft
    const bootShaft = new THREE.Mesh(
      new THREE.CylinderGeometry(0.050, 0.058, 0.16, SEG),
      mat(boc),
    );
    bootShaft.position.set(bx, 0.12, 0);
    g.add(bootShaft);
    // Boot toe cap — rounded front
    const toe = new THREE.Mesh(
      new THREE.SphereGeometry(0.055, 8, 8),
      mat(boc),
    );
    toe.position.set(bx, 0.06, -0.04);
    toe.scale.set(1.0, 0.5, 1.4);
    g.add(toe);
    // Sole
    const sole = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.025, 0.20), mat(0x141414));
    sole.position.set(bx, 0.02, -0.01);
    g.add(sole);
  }

  if (lean !== 0) {
    g.rotation.x = lean;
  }

  if (opts.overallScale && opts.overallScale !== 1) {
    g.scale.setScalar(opts.overallScale);
  }

  return g;
}

/* ------------------------------------------------------------------ */
/*  Hair builders for reuse                                            */
/* ------------------------------------------------------------------ */

export function addShortHair(group: THREE.Group, color: number, headY: number, headR: number): void {
  const hair = new THREE.Mesh(
    new THREE.SphereGeometry(headR * 1.05, SEG, 10, 0, Math.PI * 2, 0, Math.PI * 0.52),
    mat(color),
  );
  hair.position.set(0, headY + headR * 0.06, 0);
  group.add(hair);
  // Side hair volume
  for (const side of [-1, 1]) {
    const sideHair = new THREE.Mesh(
      new THREE.SphereGeometry(headR * 0.35, 8, 8),
      mat(darken(color, 0.9)),
    );
    sideHair.position.set(side * headR * 0.85, headY, headR * 0.15);
    sideHair.scale.set(0.4, 0.8, 0.7);
    group.add(sideHair);
  }
}

export function addBuzzCut(group: THREE.Group, color: number, headY: number, headR: number): void {
  const buzz = new THREE.Mesh(
    new THREE.SphereGeometry(headR * 1.02, SEG, 8, 0, Math.PI * 2, 0, Math.PI * 0.48),
    mat(color),
  );
  buzz.position.set(0, headY + headR * 0.04, 0);
  group.add(buzz);
}
